// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";

import CartLineContainer from "@flip/App/Components/Cart/CartLineContainer";
import Notify from "@flip/App/Components/Notify";
import LegacyManufacturer from "@flip/App/Components/Product/LegacyManufacturer";
import ProductMultipleSaleQty from "@flip/App/Components/Product/MultipleSaleQty";
import ProductMinimumOrderQty from "@flip/App/Components/Product/ProductMinimumOrderQty";
import ProductPreviousPurchase from "@flip/App/Components/Product/ProductPreviousPurchase";
import cartPageStyles from "@flip/App/SharedStyle/CartPageStyles";
import ProductAvailability, { ProductAvailabilityStyles } from "@flip/Overrides/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@flip/Overrides/Components/ProductBrand";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@flip/Overrides/Components/ProductPartNumbers";
import { UnitOfMeasure, UnitOfMeasureStyles } from "@flip/Overrides/Components/ProductPrice";
import CartLineQuantity, { CartLineQuantityStyles } from "@flip/Overrides/Widgets/Cart/CartLineQuantity";
import { constants } from "@flip/XDL/constants";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { isQuoteOnly, mergeWithCss, parseBoolean, xScreen } from "@flip/XDL/Utilities";
import { getSystemColor } from "@flip/XDL/Utilities/color";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useContext } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
    // XNG-Changes
    editable?: boolean;
    showSavingsPercent?: boolean;
    showSavingsAmount?: boolean;
    showInvalidPriceMessage?: boolean;
    className?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    // XNG-Changes
    removeLink?: LinkPresentationProps;
    totalSmallHeadingAndText?: SmallHeadingAndTextStyles;
    productMinimumOrderQty?: TypographyProps;
    productMultipleSaleQty?: TypographyProps;
    extendedUnitNetPriceWrapper?: InjectableCss;
    unitOfMeasure?: UnitOfMeasureStyles;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 9, 9] },
    cartLineInfoContainer: { gap: 20 },
    productInfoGridItem: { width: [12, 12, 12, 7, 7] },
    productInfoContainer: { gap: 12 },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },

    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: { width: 12 },
    productPartNumbers: {
        container: {
            gap: 10,
            css: css`
                margin-bottom: 10px;

                ${xScreen("lt", constants.cartLine.breakpoints.xs)} {
                    margin-bottom: 8px;
                    ${GridOffset} {
                        width: calc(100% - 4px);

                        ${GridItemStyle} {
                            padding: 2px;
                        }
                    }
                }
            `,
        },
    },
    quantityAndExtendedUnitNetPriceGridItem: { width: 12 },
    quantityGridItem: { width: 6 },
    extendedUnitNetPriceGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            align-items: center;
            ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                padding-right: 16px;
                text-align: right;
            }
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: {
        css: css`
            .tooltip-container span {
                margin-left: 0;
            }
        `,
    },
    productPriceAndAvailabilityGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: { width: 12 },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },
    productImage: {
        ...cartPageStyles.cartLine?.productImage,
    },
    productDescription: {
        ...cartPageStyles.cartLine?.productDescription,
    },
    quantity: {
        ...cartPageStyles.cartLine?.quantity,
    },
    addToListLink: {
        ...cartPageStyles.cartLine?.addToListLink,
    },
    removeLink: {
        ...cartPageStyles.cartLine?.removeLink,
    },
    productPrice: {
        ...cartPageStyles.cartLine?.productPrice,
    },
    totalSmallHeadingAndText: {
        ...cartPageStyles.cartLine?.totalSmallHeadingAndText,
    },
    productAvailability: {
        ...cartPageStyles.cartLine?.productAvailability,
    },
    productMinimumOrderQty: {
        css: css`
            margin-bottom: 4px;
        `,
    },
    productMultipleSaleQty: {
        css: css`
            margin-bottom: 4px;
        `,
    },
    extendedUnitNetPriceWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            [data-test-selector="cartline_extendedUnitNetPrice"] {
                width: auto;
            }
        `,
    },
    unitOfMeasure: {
        unitOfMeasureText: {
            fontFamily: fontConfig.fontFamily,
            weight: fontConfig.weight.bold,
            size: 12,
            lineHeight: "13px",
            color: "common.ui4",
            css: css`
                margin-bottom: 2px;
                white-space: nowrap;
                display: inline-block;
            `,
        },
    },
    binInformationsGridItem: {
        width: 12,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    editable = true,
    showSavingsPercent = false,
    showSavingsAmount = false,
    showInvalidPriceMessage = true,
    className,
    showPricing,
    showVmiBinDetails,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };

        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeWithCss(cartLineCardExpandedStyles, extendedStyles));
    return (
        <CartLineContainer
            extendedStyles={{
                container: {
                    css: css`
                        ${cartLine.isRestricted ? `background: ${getSystemColor("danger.light")}` : ""};
                    `,
                },
            }}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
            className={className}
        >
            <CartLineContainer.Image>
                {showImage && <ProductImage product={cartLine} extendedStyles={styles.productImage} />}
                <ProductPreviousPurchase isPreviouslyPurchased={cartLine.properties.isPreviouslyPurchased} />
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                {/* XNG-Changes:FLIP-507 */}
                {showBrand && cartLine.brand && (
                    <ProductBrand
                        brand={cartLine.brand}
                        extendedStyles={styles.productBrand}
                        isAuthorized={parseBoolean(cartLine.properties.isAuthorizedBrand)}
                    />
                )}
                {showProductTitle && (
                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                )}
                <ProductPartNumbers
                    productNumber={cartLine.manufacturerItem ? "" : cartLine.erpNumber}
                    manufacturerItem={cartLine.manufacturerItem}
                    customerProductNumber={cartLine.customerName}
                    extendedStyles={styles.productPartNumbers}
                    showProductNumber={showProductNumber}
                    // XNG-Changes
                    sku={cartLine.properties.sku}
                />
                {showVmiBinDetails && currentMode === "Vmi" && (
                    <ProductBinNumbers
                        locationId={cart.vmiLocationId}
                        binId={cartLine.vmiBinId}
                        isCondensed={false}
                        extendedStyles={styles.productBinNumbers}
                    />
                )}

                {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                    <StyledWrapper {...styles.configurationGridItem}>
                        {cartLine.sectionOptions!.map(option => (
                            <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                {`${option.sectionName}:${option.optionName}`}
                            </Typography>
                        ))}
                    </StyledWrapper>
                )}
                {/* XNG-Changes */}
                {cartLine.properties?.legacyManufacturer && (
                    <LegacyManufacturer
                        legacyManufacturer={cartLine.properties.legacyManufacturer}
                        legacyManufacturerLink={cartLine.properties.legacyManufacturerDetailUrl}
                        isAuthorized={parseBoolean(cartLine.properties.isLegacyManufacturerAnAuthorizedSupplier)}
                    />
                )}
            </CartLineContainer.Description>
            {/** XNG-Changes: FLIP-610 remove !cartLine.quoteRequired condition */}
            {showInventoryAvailability && (
                <CartLineContainer.Availability>
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        extendedStyles={styles.productAvailability}
                        // XNG-Changes
                        quoteRequired={cartLine.quoteRequired}
                    />
                </CartLineContainer.Availability>
            )}
            <CartLineContainer.MinimumOrderQty>
                <ProductMinimumOrderQty cartLine={cartLine} extendedStyles={styles.productMinimumOrderQty} />
            </CartLineContainer.MinimumOrderQty>
            <CartLineContainer.MultipleSaleQty>
                <ProductMultipleSaleQty cartLine={cartLine} extendedStyles={styles.productMultipleSaleQty} />
            </CartLineContainer.MultipleSaleQty>
            <CartLineContainer.Price>
                {!cart.cartNotPriced && (
                    <ProductPrice
                        product={cartLine}
                        currencySymbol={cart.currencySymbol}
                        showSavings={false}
                        showSavingsAmount={showSavingsAmount}
                        showSavingsPercent={showSavingsPercent}
                        showInvalidPriceMessage={showInvalidPriceMessage}
                        extendedStyles={styles.productPrice}
                        showLabel={showPriceLabel}
                        showUnitOfMeasure={showPerUnitPrice}
                        // XNG-Changes
                        showPack={false}
                        {...{ labelOverride: translate("Price/Unit") }}
                    />
                )}
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <CartLineQuantity
                    cart={cart}
                    showActions={editable}
                    editable={editable}
                    onQtyOrderedChange={qtyOrderedChangeHandler}
                    extendedStyles={styles.quantity}
                    {...{ showLabel: true }}
                />
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                {/* XNG-Changes: FLIP-999 */}
                {!isQuoteOnly(cartLine.quoteRequired, cartLine.allowZeroPricing, cartLine.pricing) &&
                cartLine.qtyOnHand > 0 &&
                !cartLine.quoteRequired &&
                !cart.cartNotPriced &&
                cartLine.pricing &&
                showPricing ? (
                    <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                        <StyledWrapper {...styles.extendedUnitNetPriceWrapper}>
                            <SmallHeadingAndText
                                extendedStyles={styles.totalSmallHeadingAndText}
                                data-test-selector="cartline_extendedUnitNetPrice"
                                heading={translate("Total")}
                                text={
                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                        : cartLine.pricing.extendedUnitNetPriceDisplay
                                }
                            />
                            <UnitOfMeasure
                                unitOfMeasure={cartLine.unitOfMeasure}
                                showUnitOfMeasure={false}
                                styles={styles.unitOfMeasure}
                            />
                        </StyledWrapper>
                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.subtotalWithoutVatText}>
                                            {cartLine.pricing.extendedUnitNetPriceDisplay}
                                        </Typography>
                                        <Typography as="p" {...styles.vatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </StyledWrapper>
                ) : (
                    <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                        <StyledWrapper {...styles.extendedUnitNetPriceWrapper}>
                            <SmallHeadingAndText
                                extendedStyles={styles.totalSmallHeadingAndText}
                                data-test-selector="cartline_extendedUnitNetPrice"
                                heading={translate("Total")}
                                text="Quote Only"
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </CartLineContainer.Total>
            {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                <CartLineContainer.LineNotesLink>
                    <CartLineNotes
                        cart={cart}
                        editable={editable}
                        onNotesChange={notesChangeHandler}
                        extendedStyles={styles.cartLineNotes}
                    />
                </CartLineContainer.LineNotesLink>
            )}
            <CartLineContainer.AddToList>
                {showAddToList && !hideAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                    <Link {...styles.addToListLink} onClick={addToListClickHandler} aria-label="Add to List">
                        {translate("Add to List")}
                    </Link>
                )}
                {cartLine.isSubscription && cartLine.productSubscription && (
                    <ProductDeliveryScheduleButton
                        subscription={
                            cartLine.properties.productSubscription
                                ? JSON.parse(cartLine.properties.productSubscription)
                                : cartLine.productSubscription
                        }
                        disabled={
                            !cart.canModifyOrder ||
                            cartLine.isPromotionItem ||
                            !cartLine.qtyOrdered ||
                            cartLine.qtyOrdered <= 0
                        }
                        onSave={saveScheduleHandler}
                        extendedStyles={styles.deliveryScheduleButton}
                    />
                )}
            </CartLineContainer.AddToList>

            <CartLineContainer.Remove>
                {showRemoveAction && (
                    <Link
                        onClick={removeCartLineClickHandler}
                        data-test-selector="cartline_removeLine"
                        {...styles.removeLink}
                        aria-label="Remove item"
                    >
                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                    </Link>
                )}
            </CartLineContainer.Remove>

            {errorMessages.length > 0 && (
                <CartLineContainer.Errors>
                    <StyledWrapper>
                        {errorMessages.map((message, index) => (
                            <Notify
                                type="danger"
                                data-test-selector="cartline_errorMessage"
                                key={`error_${cartLine.id}`}
                            >
                                {message}
                            </Notify>
                        ))}
                    </StyledWrapper>
                </CartLineContainer.Errors>
            )}
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
